import React, { useEffect, useState } from 'react'
import './styles.scss'
import './max-width.scss'
import { useTranslation } from 'react-i18next'
import { WebChatCustomElement } from '@ibm-watson/assistant-web-chat-react'
import classNames from 'classnames'
import { config, VEHICLE_TYPES } from '../../config'
import watsonAssistant from '../../utils/watson-assistant'
import ReactDOM from 'react-dom/client'
import Pills from '../pills'
import { returnCustomComponentFromType } from '../../utils/custom-response/handler'
import Launcher from './launchers/launcher'
import ChatInitialSuggestions from '../chat-initial-suggestions/chat-initial-suggestions'
import Expand from '../elements/expand'
import CollapseIcon from '../../assets/collapse.svg'
import ExpandIcon from '../../assets/expand.svg'
import { getLocal } from '../../i18n'
import { useVehicleContext } from '../../hooks/useVehicleContext'

const COOKIES_STATE = {
  MANUAL_MODEL: '_mmscanAssistantModel',
}

const suggestions = ['Tell me about safety', 'What’s S-AWC?', 'Tell me about the sound system']

function ChatWrapper({ expanded, setExpanded }) {
  const { t } = useTranslation()
  const { vehicleType } = useVehicleContext()
  const [hasHistory] = useState(() => {
    return sessionStorage.getItem('interacted-ai')
  })

  React.useEffect(() => {
    if (vehicleType) {
      try {
        const watsonInstance = watsonAssistant.getInstance()
        watsonInstance.setModel(vehicleType)
        console.log('Watson model updated to:', vehicleType)
      } catch (error) {
        // todoCT: We should handle this better, maybe retry logic or a fallback
        console.warn('Watson instance not ready yet, will set model when instance is available')
      }
    }
  }, [vehicleType])

  function renderCustomResponse(event) {
    const { message } = event.data
    return returnCustomComponentFromType(message?.user_defined?.user_defined_type, message)
  }

  return (
    <div
      className={classNames({
        'chat-wrapper': true,
        'chat-wrapper--has-history': true,
        'chat-wrapper--expanded': expanded,
      })}
    >
      {expanded && (
        <Expand
          onClick={() => setExpanded(!expanded)}
          className="chat-wrapper__expand chat-wrapper__expand-btn"
        >
          <img
            src={CollapseIcon}
            alt="Toggle AI assistant"
          />{' '}
          {t('chatWrapper.collapse')}
        </Expand>
      )}

      {expanded && (
        <div
          className="underlay"
          onClick={() => setExpanded(false)}
        ></div>
      )}
      <Launcher
        expanded={expanded}
        setExpanded={setExpanded}
      ></Launcher>

      <div className={classNames({ 'chat-wrapper__chat': true })}>
        <WebChatCustomElement
          className="chat-wrapper__chat-window"
          renderCustomResponse={renderCustomResponse}
          config={config}
          onBeforeRender={async (instance) => {
            const detectedLocale = getLocal()
            const lastLocale = await sessionStorage.getItem('m-ai-outlander-locale')
            const lastModel = await sessionStorage.getItem(COOKIES_STATE.MANUAL_MODEL)

            if (lastLocale !== detectedLocale || lastModel !== vehicleType) {
              await instance.restartConversation()
            }

            if (getLocal() === 'fr') {
              await instance.updateLocale('fr-CA')
            }
            sessionStorage.setItem('m-ai-outlander-locale', detectedLocale)
            sessionStorage.setItem(COOKIES_STATE.MANUAL_MODEL, vehicleType)

            instance.on({
              type: 'history:begin',
              handler: (event) => {
                if (
                  sessionStorage.getItem('interacted-ai') ||
                  event.messages.filter((message) => message?.input?.text).length === 0
                ) {
                  return
                }
                sessionStorage.setItem('interacted-ai', 1)
              },
            })
            watsonAssistant.setInstance(instance)

            // Set the model after Watson instance is created
            if (vehicleType) {
              try {
                const watsonInstance = watsonAssistant.getInstance()
                watsonInstance.setModel(vehicleType)
                console.log('Watson model set during initialization to:', vehicleType)
              } catch (error) {
                // todoCT: We should handle this better, maybe retry logic or a fallback
                console.log('Error setting model during initialization:', error)
              }
            }
          }}
          onAfterRender={(instance) => {
            document.body.style.visibility = 'visible'
            document.body.style.position = 'static'
            instance.changeView({ mainWindow: true, launcher: false })
          }}
        />
      </div>
      <p className="chat-wrapper__disclaimer">
        {t('chatWrapper.AICanMakeMistakes')}
        <br />
        {t('chatWrapper.checkImportantInfo')}
        <a
          href={t('chatWrapper.privacyPolicyLink')}
          target="_blank"
          aria-label="Link to privacy policy"
        >
          {t('chatWrapper.privacyPolicy')}
        </a>
      </p>
    </div>
  )
}

export default ChatWrapper
